"use client"

import { graphql, type ResultOf } from "@opensea/graphql"
import { useEffect, useState } from "react"
import { useAuthenticatedQuery } from "@/hooks/api/use-authenticated-query"
import { useSubscription } from "@/hooks/use-subscription"
import { useAddress } from "@/hooks/wallet/use-address"

const progressQuery = graphql(`
  query UseUserProgressQuery($treasureChestId: String!, $address: Address!) {
    userProgress(address: $address, treasureChestId: $treasureChestId) {
      totalPoints
      hasExceededLevelCap
      currentLevel
      currentTier
      progressPercent
    }
  }
`)

const progressSubscription = graphql(
  `
    subscription UseTreasureChestProgress($address: Address!) {
      treasureChestProgress(address: $address) {
        totalPoints
      }
    }
  `,
)

type ProgressSubscriptionResult = ResultOf<typeof progressSubscription>

export type UserProgressSummary = {
  totalPoints: number
  hasExceededLevelCap: boolean
  currentLevel: number
  currentTier: number
  progressPercent: number
}

// Returns the current progress summary for the connected wallet, with initial fetch + live updates
export function useProgress(treasureChestId?: string): {
  progress: UserProgressSummary | undefined
  refetch: () => void
} {
  const address = useAddress()
  const [progress, setProgress] = useState<UserProgressSummary | undefined>(
    undefined,
  )

  const [{ data, error }, refetch] = useAuthenticatedQuery(
    {
      query: progressQuery,
      variables: {
        address: address ?? "",
        treasureChestId: treasureChestId || "",
      },
    },
    { throwOnError: false },
  )

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch user progress:", error)
    }
  }, [error])

  useEffect(() => {
    if (data?.userProgress) {
      setProgress({
        totalPoints: data.userProgress.totalPoints,
        hasExceededLevelCap: data.userProgress.hasExceededLevelCap,
        currentLevel: data.userProgress.currentLevel,
        currentTier: data.userProgress.currentTier,
        progressPercent: data.userProgress.progressPercent,
      })
    }
  }, [data])

  // Live updates trigger a refetch to get full progress summary
  useSubscription<ProgressSubscriptionResult, { address: string }>(
    {
      query: progressSubscription,
      variables: { address: address ?? "" },
      pause: !address,
      pauseOnInactivity: false,
    },
    () => {
      // When we get a subscription update, refetch the full progress summary
      refetch({ requestPolicy: "network-only" })
    },
  )

  const refetchProgress = () => {
    if (address) {
      refetch({ requestPolicy: "network-only" })
    }
  }

  return { progress, refetch: refetchProgress }
}
